<template>
  <header class="app-header">
    <div class="header-main">
      <div class="left">
        <div class="table-info">
          <span class="label">{{ $t('table') }}</span>
          <span class="value">{{ sessionStore.tableId || '--' }}</span>
        </div>
        <div v-if="sessionStore.mode === 'collaborative'" class="pin-info">
          <span class="label">{{ $t('pinCode') }}</span>
          <span class="value">{{ sessionStore.pinCode }}</span>
        </div>
      </div>
      
      <div class="right">
        <button class="leave-btn" @click="leaveTable" :title="$t('exit')">
          {{ $t('exit') }}
        </button>

        <div class="active-users" v-if="tableStore.activeUsers.length > 0" @click="showUsersModal = true">
          <div 
            v-for="user in tableStore.activeUsers" 
            :key="user.id" 
            class="user-avatar"
            :title="user.name"
          >
            {{ user.name.charAt(0).toUpperCase() }}
          </div>
        </div>

        <div v-if="sessionStore.currentUser" class="user-info">
          <span class="user-name">{{ sessionStore.currentUser.name }}</span>
        </div>
        <button class="cart-btn" @click="tableStore.toggleCart()">
          🛒
          <span v-if="tableStore.itemCount > 0" class="cart-pill">
            {{ tableStore.itemCount }}
          </span>
        </button>
      </div>
    </div>
    <slot name="bottom"></slot>

    <!-- Users Modal -->
    <transition name="modal-fade">
      <div v-if="showUsersModal" class="modal-overlay" @click="showUsersModal = false">
        <div class="users-modal" @click.stop>
          <div class="modal-header">
            <h3>{{ $t('tableParticipants') }}</h3>
            <button class="close-btn" @click="showUsersModal = false">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="users-list">
            <div v-for="user in allParticipants" :key="user.id" class="user-row">
              <div class="user-avatar-lg" :class="{ host: user.isHost }">
                {{ user.name.charAt(0).toUpperCase() }}
              </div>
              <div class="user-details">
                <span class="user-name-lg">{{ user.name }}</span>
                <div class="user-badges">
                  <span v-if="user.isHost" class="badge host-badge">{{ $t('host') }}</span>
                  <span v-if="user.isOnline" class="badge online-badge">{{ $t('online') }}</span>
                  <span v-else class="badge offline-badge">{{ $t('offline') }}</span>
                  <span v-if="user.itemCount > 0" class="badge items-badge">{{ user.itemCount }} {{ $t('items') }}</span>
                </div>
              </div>
              <button 
                v-if="isCurrentUserHost && !user.isHost" 
                class="make-host-btn"
                @click="transferHost(user)"
                :disabled="isTransferring"
              >
                {{ $t('makeHost') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </header>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useSessionStore } from '@/stores/session'
import { useTableStore } from '@/stores/table'
import { useRouter } from 'vue-router'


const sessionStore = useSessionStore()
const tableStore = useTableStore()
const router = useRouter()
const showUsersModal = ref(false)
const isTransferring = ref(false)

// Check if current user is the host (read from Firebase-synced users, not local state)
const isCurrentUserHost = computed(() => {
  if (!sessionStore.currentUser?.id) return false
  const userFromFirebase = tableStore.users[sessionStore.currentUser.id]
  return userFromFirebase?.isHost === true
})

// Combine active users with users who have added items to cart
const allParticipants = computed(() => {
  const participants = new Map()
  
  // Add all registered users from the session
  Object.values(tableStore.users).forEach(user => {
    participants.set(user.id, {
      ...user,
      isOnline: false,
      itemCount: 0
    })
  })
  
  // Mark active users as online
  tableStore.activeUsers.forEach(user => {
    if (participants.has(user.id)) {
      participants.get(user.id).isOnline = true
    } else {
      participants.set(user.id, {
        ...user,
        isOnline: true,
        itemCount: 0
      })
    }
  })
  
  // Count items per user from cart
  tableStore.cartItems.forEach(item => {
    const userId = item.addedBy
    if (participants.has(userId)) {
      participants.get(userId).itemCount += item.quantity
    } else {
      // User added items but isn't in the users list (edge case)
      participants.set(userId, {
        id: userId,
        name: item.addedByName || 'Unknown',
        isHost: false,
        isOnline: false,
        itemCount: item.quantity
      })
    }
  })
  
  // Sort: host first, then online users, then by name
  return Array.from(participants.values()).sort((a, b) => {
    if (a.isHost !== b.isHost) return a.isHost ? -1 : 1
    if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1
    return a.name.localeCompare(b.name)
  })
})

const transferHost = async (newHost) => {
  if (!isCurrentUserHost.value || isTransferring.value) return
  
  isTransferring.value = true
  try {
    await tableStore.transferHost(sessionStore.currentUser.id, newHost.id)
    // Host status is now derived from Firebase-synced tableStore.users, no local update needed
  } catch (error) {
    console.error('Failed to transfer host:', error)
    alert('Failed to transfer host. Please try again.')
  } finally {
    isTransferring.value = false
  }
}


const leaveTable = async () => {
  // In a real app, use a custom modal. For now, we'll skip the native confirm to ensure it works.
  // if (confirm('Are you sure you want to leave this table?')) {
    const slug = sessionStore.restaurantSlug || 'demo'
    await tableStore.unbindTable()
    sessionStore.leaveSession()
    router.push(`/${slug}`)
  // }
}
</script>

<style scoped>
.app-header {
  display: flex;
  flex-direction: column;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  z-index: 100;
  height: auto;
  box-sizing: border-box;
}

.header-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  width: 100%;
  height: 80px;
  box-sizing: border-box;
}

.left {
  display: flex;
  gap: 1rem;
}

.table-info, .pin-info {
  display: flex;
  flex-direction: column;
}

.label {
  font-size: 0.7rem;
  color: #888;
  text-transform: uppercase;
}

.value {
  font-weight: 700;
  font-size: 1.1rem;
  color: var(--primary-color);
}

.right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.user-name {
  font-weight: 600;
  font-size: 0.9rem;
}

.cart-btn {
  position: relative;
  background: #f0f0f0;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.cart-btn:hover {
  background: #e0e0e0;
}

.cart-pill {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #ff4757;
  color: white;
  font-size: 0.7rem;
  font-weight: bold;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  border: 2px solid white;
}

.active-users {
  display: flex;
  margin-right: 0.5rem;
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: bold;
  border: 2px solid white;
  margin-left: -10px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.user-avatar:first-child {
  margin-left: 0;
}

.leave-btn {
  background: none;
  border: 1px solid #ddd;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  color: #666;
  cursor: pointer;
  margin-right: 0.5rem;
  transition: all 0.2s;
}

.leave-btn:hover {
  background: #fff0f0;
  color: #e74c3c;
  border-color: #e74c3c;
}

.lang-btn {
  background: none;
  border: 1px solid #ddd;
  padding: 6px;
  border-radius: 6px;
  font-size: 1.2rem;
  cursor: pointer;
  margin-right: 0.5rem;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 32px;
}

.lang-btn:hover {
  background: #f8f9fa;
  border-color: #cbd5e1;
}

.active-users {
  cursor: pointer;
  transition: transform 0.2s;
}

.active-users:hover {
  transform: scale(1.05);
}

/* Users Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 100px;
  z-index: 200;
}

.users-modal {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 360px;
  max-height: 70vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.1rem;
  color: var(--secondary-color);
}

.close-btn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f5f5f5;
  color: #333;
}

.users-list {
  padding: 12px;
  overflow-y: auto;
  max-height: calc(70vh - 60px);
}

.user-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  transition: background 0.2s;
}

.user-row:hover {
  background: #f8f9fa;
}

.make-host-btn {
  background: none;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
}

.make-host-btn:hover:not(:disabled) {
  background: var(--primary-color);
  color: white;
}

.make-host-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.user-avatar-lg {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: bold;
  flex-shrink: 0;
}

.user-avatar-lg.host {
  background: linear-gradient(135deg, #FFD700, #FFA500);
  box-shadow: 0 2px 8px rgba(255, 165, 0, 0.4);
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-name-lg {
  font-weight: 600;
  color: var(--secondary-color);
  display: block;
  margin-bottom: 4px;
}

.user-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.badge {
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 500;
}

.host-badge {
  background: #FEF3C7;
  color: #92400E;
}

.online-badge {
  background: #D1FAE5;
  color: #065F46;
}

.offline-badge {
  background: #F3F4F6;
  color: #6B7280;
}

.items-badge {
  background: #EDE9FE;
  color: #5B21B6;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
